import { IEpticaDeprecatedService } from '@agent/core/channel-digit/mail/eptica/eptica-deprecated.service';
import { IEpticaRequestFrame } from '@agent/core/channel-digit/mail/eptica/models/eptica.request.interfaces';
import { HttpDeprecatedService } from '@commons/core/transport/http-deprecated.service';
import { IEpticaExctractEvent, IEpticaExtractHistoryData } from '@commons/history/history-mail/models/eptica-extract';
import { ITimelineEntry, TimelineEntry, TimelineType } from '@commons/history/timeline//models/timeline-entry';
import { ITimelineEntryResult } from '@commons/history/timeline//models/timeline-entry-result';
import { IAppointment } from '@commons/models/appointment';
import { IContact } from '@commons/models/contact';
import {
  ContactEntryNotHandledReason,
  ContactEntryVisibilityType,
  IContactEntry,
  IContactEntryContent,
} from '@commons/models/contact-entry';
import { InteractionStatus } from '@commons/models/contact-interaction';
import { joiner } from '@commons/util/helper/string';
import { RxUtil } from '@commons/util/utilities/rx';
import { fromJson, ILogService, IPromise, IQService, isArray, isDefined, isUndefined } from 'angular';
import {
  catchError,
  combineLatest,
  concatMap,
  delay,
  distinct,
  EMPTY,
  filter,
  first,
  from as observableFrom,
  map,
  merge,
  mergeMap,
  Observable,
  of,
  OperatorFunction,
  pipe,
  retryWhen,
  scan,
  Subscription,
  switchMap,
  take,
  tap,
  throwError,
  timeout,
  toArray,
} from 'rxjs';

import {
  Campaign,
  CampaignType,
  ChannelDirection,
  ChannelType,
  CustomerFacade,
  ElasticSearchService,
  EpticaExposedRequest,
  extractEpticaField,
  MemoDtoUtil,
  None,
  Option,
  randomUUID,
  TimeSlot,
} from '@contact/upgrade';

import { IAppointmentDeprecatedService } from '../appointment-deprecated.service';
import { IAssociatedRequest } from './models/associated-request';
import { IContactEntryResult } from './models/entry-result';
import { ISortByNameAndOrder } from './models/history-models';

interface SearchParamsBasic {
  readonly interactionTypeList?: string[];
  readonly interactionStatusList?: InteractionStatus[];
  readonly canalName?: ChannelType;
  readonly from?: number;
  readonly size?: number;
  readonly entriesToMatch?: ITimelineEntry[];
}

interface SearchParamsContact extends SearchParamsBasic {
  readonly contact: IContact;
}

interface SearchParamsCompany extends SearchParamsBasic {
  readonly companyName: any;
}

type SearchParams = SearchParamsContact | SearchParamsCompany;

export interface IContactHistoryService {
  searchPreviousContactExchangeByChannel(input: SearchParams): Observable<IContactEntryResult>;

  searchPreviousContactExchangesByQuery(query: string): Observable<IContactEntryResult>;

  searchByConnectionId(connectionId: string): Observable<IContactEntryResult>;

  searchByEpticaRequestIds(epticaRequestIds: string[]): Observable<IContactEntryResult>;

  searchByEpticaRequestId(epticaRequestId: string): Observable<IContactEntry | undefined>;

  searchOpenedInteractionById(interactionId: string): IPromise<IContactEntryResult>;

  searchClosedInteractionById(interactionId: string): IPromise<IContactEntryResult>;

  searchByParentUuid(parentUuid: string, from?: number, size?: number): Observable<IContactEntry[]>;

  findInProcess(input: SearchParams): Observable<ITimelineEntryResult>;

  findHistory(input: SearchParams): Observable<ITimelineEntryResult>;

  findTreatments(entry: IContactEntry, epticaPromise: IPromise<IEpticaRequestFrame>): Observable<ITimelineEntryResult>;

  getInteractionDescription(entry: IContactEntry): Option<string>;

  getRepositoryNames(domain: string): IPromise<string>;

  retrieveInteractionDetails(interactionId: string): Observable<ITimelineEntry>;

  openExchangeInNewWindow(entry: IContactEntry): void;
}

export class ContactHistoryService implements IContactHistoryService {
  static $inject: Array<string> = [
    '$log',
    '$q',
    'httpDeprecated',
    'elasticSearchService',
    'epticaDeprecatedService',
    'appointmentDeprecatedService',
    'customerFacade',
    'configuration',
  ];

  DEFAULT_SORT: ISortByNameAndOrder[] = [
    {
      name: 'entryDate',
      order: 'desc',
    },
  ];
  private buildTimelineResult = (timeline: ITimelineEntry[], type: TimelineType, total?: number): ITimelineEntryResult => {
    let myVals = {};
    let uniqTimeline = timeline.filter((entry: ITimelineEntry) => {
      // uniq filter
      if (myVals[entry.id]) {
        return false;
      }
      myVals[entry.id] = true;
      return true;
    });
    return {
      timeline: uniqTimeline,
      total: total || uniqTimeline.length,
      type: type,
    };
  };

  constructor(
    private readonly $log: ILogService,
    private readonly $q: IQService,
    private readonly httpDeprecated: HttpDeprecatedService,
    private readonly elasticSearchService: ElasticSearchService,
    private readonly epticaDeprecatedService: IEpticaDeprecatedService,
    private readonly appointmentDeprecatedService: IAppointmentDeprecatedService,
    private readonly customerFacade: CustomerFacade,
    private readonly configuration: any
  ) {}

  getRepositoryNames(domain: string): IPromise<string> {
    return this.httpDeprecated
      .asPromise()
      .withConfig({
        headers: {
          'Content-Type': 'text/plain; charset=UTF-8',
          Accept: 'text/plain; charset=UTF-8',
        },
      })
      .get('facade/public/tools/getCustomerRepositoryNameFromDomain', [domain])
      .then(result => result.data);
  }

  searchPreviousContactExchangesByQuery(query: string): Observable<IContactEntryResult> {
    return this.elasticSearchService.searchOnSeveralTypesDeprecated(query).pipe(
      map(data => {
        let entries = ContactHistoryService.buildEntries(data);
        return {
          contactEntries: entries,
          total: entries.length,
        };
      }),
      tap(
        next => this.$log.debug(`[agent-history] searchPreviousContactExchangesByQuery found : [${JSON.stringify(next)}]`),
        error => this.$log.error(`[agent-history] searchPreviousContactExchangesByQuery failed : [${JSON.stringify(error)}]`),
        () => this.$log.debug(`[agent-history] searchPreviousContactExchangesByQuery done`)
      )
    );
  }

  searchPreviousContactExchangeByChannel(input: SearchParams): Observable<IContactEntryResult> {
    let searchParams: any;
    if ('companyName' in input) {
      searchParams = {
        types: ['contact_entry'],
        sort: [{ entryDate: { order: 'desc' } }],
        from: input.from,
        size: input.size,
        query: {
          bool: {
            must: [{ term: { 'contactCompanyName.keyword': input.companyName } }],
          },
        },
      };
    } else {
      searchParams = {
        types: ['contact_entry'],
        sort: [{ entryDate: { order: 'desc' } }],
        from: input.from,
        size: input.size,
        query: {
          bool: {
            must: [{ term: { contactUuid: input.contact ? input.contact.customerId : '' } }],
          },
        },
      };
    }

    if (input.interactionTypeList && input.interactionTypeList.length > 0) {
      let should: any = [];
      input.interactionTypeList.forEach(interaction => {
        should.push({ term: { interactionType: interaction } });
      });
      searchParams.query.bool.must.push({ bool: { should: should } });
    }

    if (input.canalName) {
      searchParams.query.bool.must.push({ term: { canalName: ChannelType[input.canalName as string] } });
    }

    let interactionStatusList = input.interactionStatusList;
    if (interactionStatusList && interactionStatusList.length > 0) {
      let should: any = [];
      interactionStatusList.forEach(interactionStatus => {
        should.push({ term: { status: InteractionStatus[interactionStatus] } });
      });
      searchParams.query.bool.must.push({ bool: { should: should } });
    }

    return this.elasticSearchService.searchOnSeveralTypesDeprecated(searchParams).pipe(
      map(data => {
        let entries = ContactHistoryService.buildEntries(data);
        return {
          contactEntries: entries,
          total: data.hits.total.value || entries.length,
        };
      }),
      tap(
        next =>
          this.$log.debug(
            `[agent-history] searchPreviousContactExchangeByChannel contactId[${
              'contact' in input ? input.contact.customerId : ''
            }], interactionType[${JSON.stringify(input.interactionTypeList)}], interactionStatus[${JSON.stringify(
              input.interactionStatusList
            )}], canal[${input.canalName}] found : [${JSON.stringify(next)}]`
          ),
        error =>
          this.$log.error(
            `[agent-history] searchPreviousContactExchangeByChannel contactId[${
              'contact' in input ? input.contact.customerId : ''
            }], interactionType[${JSON.stringify(input.interactionTypeList)}], interactionStatus[${JSON.stringify(
              input.interactionStatusList
            )}], canal[${input.canalName}] failed : [${JSON.stringify(error)}]`
          ),
        () =>
          this.$log.debug(
            `[agent-history] searchPreviousContactExchangeByChannel contactId[${
              'contact' in input ? input.contact.customerId : ''
            }], interactionType[${JSON.stringify(input.interactionTypeList)}], interactionStatus[${JSON.stringify(
              input.interactionStatusList
            )}], canal[${input.canalName}] done`
          )
      )
    );
  }

  private searchInteractionById(interactionId: string): Observable<IContactEntryResult> {
    let searchParams: any = {
      types: ['contact_entry'],
      sort: [{ entryDate: { order: 'desc' } }],
      from: 0,
      query: {
        bool: {
          must: [{ term: { uuid: interactionId } }],
        },
      },
    };
    return this.elasticSearchService.searchOnSeveralTypesDeprecated(searchParams).pipe(
      map(data => {
        let entries = ContactHistoryService.buildEntries(data);
        return {
          contactEntries: entries,
          total: entries.length,
        };
      }),
      tap(
        next => this.$log.debug(`[agent-history] searchInteractionById uuid[${interactionId}] found : [${JSON.stringify(next)}]`),
        error => this.$log.error(`[agent-history] searchInteractionById uuid[${interactionId}] failed : [${JSON.stringify(error)}]`),
        () => this.$log.debug(`[agent-history] searchInteractionById uuid[${interactionId}] done`)
      )
    );
  }

  searchPreviousContactExchange(
    contactUuid: string,
    interactionTypeList: string[],
    interactionStatusList: InteractionStatus[],
    from = 1,
    size = 15
  ): Observable<IContactEntryResult> {
    const input = {
      interactionTypeList: interactionTypeList,
      interactionStatusList: interactionStatusList,
      canalName: undefined,
      from: from,
      size: size,
      contactId: contactUuid,
      companyName: undefined,
    };
    return this.searchPreviousContactExchangeByChannel(input);
  }

  searchById(id: string): Observable<IContactEntry[]> {
    if (isUndefined(id)) {
      return EMPTY;
    }
    let searchParams: any = {
      types: ['contact_entry'],
      sort: [{ entryDate: { order: 'desc' } }],
      from: 0,
      size: 1,
      query: {
        bool: {
          must: {
            term: { uuid: id },
          },
        },
      },
    };
    return this.elasticSearchService.searchOnSeveralTypesDeprecated(searchParams).pipe(
      map(data => ContactHistoryService.buildEntries(data)),
      tap(
        next => this.$log.debug(`[agent-history] searchById contactId[${id}] found : [${JSON.stringify(next)}]`),
        error => this.$log.error(`[agent-history] searchById contactId[${id}] failed : [${JSON.stringify(error)}]`),
        () => this.$log.debug(`[agent-history] searchById contactId[${id}] done`)
      )
    );
  }

  searchByConnectionId(connectionId: string): Observable<IContactEntryResult> {
    if (isUndefined(connectionId)) return EMPTY;
    let searchParams: any = {
      types: ['contact_entry'],
      sort: [{ entryDate: { order: 'desc' } }],
      from: 0,
      size: 1,
      query: {
        bool: {
          must: {
            term: { connectionId: connectionId },
          },
        },
      },
    };
    return this.elasticSearchService.searchOnSeveralTypesDeprecated(searchParams).pipe(
      map(data => ({
        contactEntries: ContactHistoryService.buildEntries(data),
        total: data.hits.total.value,
      })),
      tap(
        next => this.$log.debug(`[agent-history] searchByConnectionId connectionId[${connectionId}] found : [${JSON.stringify(next)}]`),
        error => this.$log.error(`[agent-history] searchByConnectionId connectionId[${connectionId}] failed : [${JSON.stringify(error)}]`),
        () => this.$log.debug(`[agent-history] searchByConnectionId connectionId[${connectionId}] done`)
      )
    );
  }

  searchByEpticaRequestIds(epticaRequestIds: string[]): Observable<IContactEntryResult> {
    if (isUndefined(epticaRequestIds)) {
      return EMPTY;
    }
    let searchParams: any = {
      types: ['contact_entry'],
      sort: [{ entryDate: { order: 'desc' } }],
      from: 0,
      query: {
        bool: {
          must: [{ terms: { epticaRequestId: epticaRequestIds } }],
        },
      },
    };
    return this.elasticSearchService.searchOnSeveralTypesDeprecated(searchParams).pipe(
      map(data => ({
        contactEntries: ContactHistoryService.buildEntries(data),
        total: data.hits.total.value,
      }))
    );
  }

  searchByEpticaRequestId(epticaRequestId: string): Observable<IContactEntry | undefined> {
    const searchParams: any = {
      types: ['contact_entry'],
      sort: [{ entryDate: { order: 'desc' } }],
      from: 0,
      query: {
        bool: {
          must: [{ term: { epticaRequestId: epticaRequestId } }],
        },
      },
    };

    return this.elasticSearchService
      .searchOnSeveralTypesDeprecated(searchParams)
      .pipe(map(data => ContactHistoryService.buildEntries(data).shift()));
  }

  searchClosedInteractionById(interactionId: string): IPromise<IContactEntryResult> {
    return this.searchInteractionByIdAndStatus(interactionId, InteractionStatus.CLOSED);
  }

  searchOpenedInteractionById(interactionId: string): IPromise<IContactEntryResult> {
    return this.searchInteractionByIdAndStatus(interactionId, InteractionStatus.OPENED);
  }

  searchByParentUuid(parentUuid: string, from: number = 0, size: number = 50): Observable<IContactEntry[]> {
    if (isUndefined(parentUuid)) {
      return EMPTY;
    }
    let searchParams: any = {
      types: ['contact_entry'],
      sort: [{ entryDate: { order: 'desc' } }],
      from: from,
      size: size,
      query: {
        bool: {
          must: {
            term: { parentUuid: parentUuid },
          },
        },
      },
    };
    return this.elasticSearchService.searchOnSeveralTypesDeprecated(searchParams).pipe(
      map(data => ContactHistoryService.buildEntries(data)),
      tap(
        next => this.$log.debug(`[agent-history] searchByParentUuid parentUuid[${parentUuid}] found : [${JSON.stringify(next)}]`),
        error => this.$log.error(`[agent-history] searchByParentUuid parentUuid[${parentUuid}] failed : [${JSON.stringify(error)}]`),
        () => this.$log.debug(`[agent-history] searchByParentUuid parentUuid[${parentUuid}] done`)
      )
    );
  }

  /**
   * Build the inProcess list of elements composed of appointments and mails
   */
  findInProcess(input: SearchParams): Observable<ITimelineEntryResult> {
    // Observable in process appointment
    let queryAppointment$: Observable<ITimelineEntryResult> = this.appointmentInProcess$(input).pipe(
      tap(
        next => this.$log.debug(`[AgentHistory] findInProcess - Appointment - NEXT : ${JSON.stringify(next)}`),
        err => this.$log.debug(`[AgentHistory] findInProcess - Appointment - ERROR : ${JSON.stringify(err)}`),
        () => this.$log.debug(`[AgentHistory] findInProcess - Appointment - COMPLETE`)
      ),
      catchError(() => EMPTY)
    );
    // Looking for all OPENED SOCIAL_NETWORK linked to the selected contact
    let querySocialNetwork$: Observable<ITimelineEntryResult> = this.socialNetworkInProcess$(input).pipe(
      tap(
        next => this.$log.debug(`[AgentHistory] findInProcess - Social Network - NEXT : ${JSON.stringify(next)}`),
        err => this.$log.debug(`[AgentHistory] findInProcess - Social Network - ERROR : ${JSON.stringify(err)}`),
        () => this.$log.debug(`[AgentHistory] findInProcess - Social Network - COMPLETE`)
      ),
      catchError(() => EMPTY)
    );
    let param;
    param = { from: input.from, size: input.size };
    if ('companyName' in input) {
      param.companyName = input.companyName;
    } else {
      param.contact = input.contact;
    }
    // Looking for all OPENED Mails linked to the selected contact according to  companyName
    let queryMail$: Observable<ITimelineEntryResult> = this.mailInProcess$(param).pipe(
      tap(
        next => this.$log.debug(`[AgentHistory] findInProcess - Mail - NEXT : ${JSON.stringify(next)}`),
        err => this.$log.debug(`[AgentHistory] findInProcess - Mail - ERROR : ${JSON.stringify(err)}`),
        () => this.$log.debug(`[AgentHistory] findInProcess - Mail - COMPLETE`)
      ),
      catchError(() => EMPTY)
    );
    return merge(queryAppointment$, this.retry(queryMail$, input.entriesToMatch), querySocialNetwork$).pipe(this.accumulate());
  }

  retrieveInteractionDetails(interactionId: string): Observable<ITimelineEntry> {
    if (!interactionId || interactionId.length === 0) {
      return throwError(() => 'parameter error');
    }
    return this.searchInteractionById(interactionId).pipe(
      map((result: IContactEntryResult) => {
        if (result.total === 0) {
          return throwError(() => 'parameter error');
        } else {
          return result.contactEntries[0];
        }
      }),
      mergeMap((contactEntry: IContactEntry) => {
        switch (contactEntry.canalName) {
          case ChannelType[ChannelType.MAIL]:
            if (contactEntry.visibilityType === ContactEntryVisibilityType.PRIVATE || contactEntry.visibilityType === 'PRIVATE') {
              contactEntry.isEpticaSynchronized = false;
              return of(TimelineEntry.fromContactEntry(contactEntry, '', false));
            }
            if (!!contactEntry.epticaRequestId && !isNaN(parseInt(`${contactEntry.epticaRequestId}`, 10))) {
              let visibility: boolean = this.epticaDeprecatedService.getRequestVisibility([
                parseInt(`${contactEntry.epticaRequestId}`, 10),
              ])[0];
              if (contactEntry.interactionStatus === InteractionStatus[InteractionStatus.CLOSED]) {
                return of(TimelineEntry.fromContactEntry(contactEntry, this.getSubject(contactEntry).getOrElse(''), visibility));
              } else if (
                contactEntry.interactionStatus === InteractionStatus[InteractionStatus.OPENED] ||
                contactEntry.interactionStatus === InteractionStatus[InteractionStatus.IN_APPROVAL]
              ) {
                contactEntry.isEpticaSynchronized = false;
                return this.epticaDeprecatedService.getMatchingRequestsByIds([contactEntry.epticaRequestId], 'id', 'asc', 0, 1).pipe(
                  map((requests: EpticaExposedRequest[]) => {
                    if (requests.length > 0) {
                      let request = {
                        id: +requests[0].id,
                        responsibleId: requests[0].accountResponsible && +requests[0].accountResponsible.id,
                        subject: requests[0].subject,
                        memo: MemoDtoUtil.buildMemo(extractEpticaField(requests[0].fields, 'awlDifferMemo')),
                      } as IAssociatedRequest;
                      contactEntry.isEpticaSynchronized = true;
                      return TimelineEntry.fromContactEntry(contactEntry, request.subject, visibility, request.responsibleId, request.memo);
                    } else {
                      return TimelineEntry.fromContactEntry(
                        contactEntry,
                        'not synchronized:' + this.getSubject(contactEntry).getOrElse(''),
                        false
                      );
                    }
                  })
                );
              }
              return throwError(() => 'parameter error');
            }
            return throwError(() => 'parameter error');
            break;
          default:
            return of(TimelineEntry.fromContactEntry(contactEntry));
        }
      })
    );
  }

  findHistory(input: SearchParams): Observable<ITimelineEntryResult> {
    // Observable history
    let requestVisibility: { [id: string]: boolean } = {};
    let param;
    param = { ...input, interactionTypeList: ['PRIMARY', 'LINKED'], interactionStatusList: [InteractionStatus.CLOSED] };
    let queryHistory$: Observable<ITimelineEntryResult> = this.searchPreviousContactExchangeByChannel(param).pipe(
      // Retrieve visibility
      mergeMap((result: IContactEntryResult) =>
        RxUtil.fromPromise(() =>
          this.getRequestVisibility(result.contactEntries.map(r => parseInt(`${r.epticaRequestId}`, 10)).filter(e => !isNaN(e)))
            .then(r => (requestVisibility = r))
            .then(() => result)
        )
      ),
      mergeMap((result: IContactEntryResult) =>
        observableFrom(result.contactEntries).pipe(
          distinct((entry: IContactEntry) => entry.id),
          map((entry: IContactEntry) => {
            if (entry.canalName === 'MAIL') {
              return TimelineEntry.fromContactEntry(
                entry,
                this.getSubject(entry).getOrElse(''),
                this.hasVisibility(requestVisibility, `${entry.epticaRequestId}`, true)
              );
            }
            return TimelineEntry.fromContactEntry(entry);
          }),
          toArray(),
          // build result
          map((timeline: ITimelineEntry[]) => this.buildTimelineResult(timeline, 'HISTO', result.total))
        )
      ),
      catchError(() => EMPTY)
    );

    // return empty observable if condition isn't true

    return this.retry(queryHistory$, input['entriesToMatch']);
  }

  findTreatments(entry: IContactEntry, epticaPromise: IPromise<IEpticaRequestFrame>): Observable<ITimelineEntryResult> {
    let child$: Observable<IContactEntry[]> = this.searchByParentUuid(entry.id);
    let parent$: Observable<IContactEntry[]> = this.searchById(entry.parentInteractionUuid || '').pipe(
      // force interaction type
      // interaction parent must be in  treatment list linked
      mergeMap((data: IContactEntry[]) => data),
      map((data: IContactEntry) => {
        data.interactionType = 'LINKED';
        return data;
      }),
      toArray()
    );

    // Observable to join parent and child interaction
    let requestVisibility: { [id: string]: boolean } = {};
    let treatment$: Observable<ITimelineEntryResult> = merge(child$, parent$).pipe(
      mergeMap((entries: IContactEntry[]) =>
        RxUtil.fromPromise(() =>
          this.getRequestVisibility(entries.map(e => parseInt(`${e.epticaRequestId}`, 10)).filter(e => !isNaN(e)))
            .then(r => (requestVisibility = r))
            .then(() => entries)
        )
      ),
      mergeMap((entries: IContactEntry[]) => entries),
      map((e: IContactEntry) => {
        if (e.canalName === 'MAIL') {
          return TimelineEntry.fromContactEntry(
            e,
            this.getSubject(e).getOrElse(''),
            this.hasVisibility(requestVisibility, `${e.epticaRequestId}`, true)
          );
        }
        return TimelineEntry.fromContactEntry(e);
      }),
      toArray(),
      // build result
      map((timeline: ITimelineEntry[]) => this.buildTimelineResult(timeline, 'TREATMENT')),
      catchError(() => EMPTY)
    );

    // Observable to extract histo frame
    let mailTreatment$: Observable<ITimelineEntryResult> = of(epticaPromise).pipe(
      // check eptica promise
      filter(promise => promise != null),
      mergeMap(promise => RxUtil.fromPromise(() => promise)),
      filter(request => entry.epticaRequestId === request.getId().toString()),
      mergeMap(request => request.retrieveEvents()),
      map(events =>
        events.map((evt: IEpticaExctractEvent) => {
          let fakeEntry: IContactEntry = ContactHistoryService.buildFakeContactEntry();
          fakeEntry.entryDate = evt.date;
          fakeEntry.interactionType = 'FAKE_MAIL_ENTRY';
          fakeEntry.description = evt.description.title;
          fakeEntry.canalName = ChannelType[ChannelType.MAIL].toUpperCase();
          fakeEntry.canalDirection = ChannelDirection.INCOMING;

          let timeline: ITimelineEntry = TimelineEntry.fromContactEntry(fakeEntry);

          if (this.validateMailEvent(evt)) {
            timeline.mailComment = evt;
          }

          return timeline;
        })
      ),
      map((timeline: ITimelineEntry[]) => this.buildTimelineResult(timeline, 'TREATMENT')),
      catchError(() => EMPTY)
    );

    return merge(treatment$, mailTreatment$).pipe(this.accumulate());
  }

  public getInteractionDescription(entry: IContactEntry): Option<string> {
    let desc: string = '';
    if (entry) {
      switch (entry.canalName) {
        case 'MAIL':
          desc = this.getSubject(entry).getOrElse('');
          break;
        case 'PHONE':
        case 'UNITARY_PHONE':
          if (entry.canalDirection === 'OUTGOING') {
            desc = entry.description;
          }
          break;
        default:
          break;
      }
    }
    return Option.of(desc);
  }

  openExchangeInNewWindow(entry: IContactEntry): void {
    const exchangeViewerUrl = this.configuration.exchangeViewerUrl;
    if (!exchangeViewerUrl) {
      this.$log.warn(
        `[ContactHistoryService] can't open exchange viewer because no path found in 'configuration.json' with key 'exchangeViewerUrl'`
      );
      return;
    }
    let origin: string | undefined;
    let pathname: string | undefined;
    try {
      // parse url
      let url = new URL(exchangeViewerUrl);
      origin = url.origin;
      pathname = joiner([this.removeSlash(url.pathname), this.removeSlash(url.hash)], '/');
    } catch (_ignore) {
      // if error, we assume 'exchangeViewerUrl' is a path
      // we use the origin as host
      origin = window.location.origin;
      pathname = this.removeSlash(exchangeViewerUrl);
    }
    const url = joiner([origin, pathname, entry.id], '/');
    this.$log.debug(`[ContactHistoryService] open exchange viewer with url [${url}]`);
    window.open(url, '_blank');
  }

  private removeSlash(str: string): string {
    // remove first and last '/'
    return str.replace(/^\//, '').replace(/\/$/, '');
  }

  static buildEntry(_source: any): IContactEntry {
    return {
      id: _source.uuid,
      entryDate: (_source.entryDate && new Date(_source.entryDate)) || new Date(),
      beginDate: (_source.startDate && new Date(_source.startDate)) || new Date(),
      endDate: (_source.endDate && new Date(_source.endDate)) || new Date(),
      canalName: _source.canalName,
      canalDirection: _source.canalDirection,
      contactUri: _source.contactUri,
      contactFirstName: _source.contactFirstName,
      contactLastName: _source.contactLastName,
      contactCompanyName: _source.contactCompanyName,
      contactUuid: _source.contactUuid,
      csrUuid: _source.csrUuid,
      csrLogin: _source.csrLogin,
      csrFirstname: _source.csrFirstName,
      csrLastname: _source.csrLastName,
      skills: _source.skills,
      campaignType: _source.campaignType,
      description: _source.description,
      technicalId: _source.technicalId,
      connectionId: _source.connectionId,
      epticaRequestId: _source.epticaRequestId || '',
      dimeloRequestId: _source.dimeloRequestId || '',
      interactionType: _source.interactionType || '',
      interactionStatus: _source.status || '',
      contents: _source.contents,
      parentInteractionUuid: _source.parentUuid || '',
      redirected: _source.redirected,
      visibilityType: ContactEntryVisibilityType[_source.contactEntryVisibilityType],
      isEpticaSynchronized: true,
      chatConversationUuid: _source.chatConversationUuid,
      notHandledReason: ContactEntryNotHandledReason[_source.notHandledReason] || ContactEntryNotHandledReason.NONE,
      delayBeforeNotHandledReason: _source.delayBeforeNotHandledReason,
      extraData: _source.extraData,
    };
  }

  static buildEntries(data): IContactEntry[] {
    if (
      !!data &&
      isDefined(data.hits) &&
      isDefined(data.hits.total) &&
      isDefined(data.hits.total.value) &&
      data.hits.total.value > 0 &&
      isArray(data.hits.hits)
    ) {
      return data.hits.hits.map(hit => ContactHistoryService.buildEntry(hit._source));
    }
    return [];
  }

  private searchInteractionByIdAndStatus(
    interactionId: string,
    status: InteractionStatus = InteractionStatus.CLOSED
  ): IPromise<IContactEntryResult> {
    this.$log.debug(
      '[agent-history] searchInteractionByIdAndStatus with id[' + interactionId + '] and status[' + InteractionStatus[status] + ']'
    );

    let deferred = this.$q.defer<IContactEntryResult>();

    if (isUndefined(interactionId) || interactionId === '') {
      deferred.reject('[agent-history] searchInteractionByIdAndStatus bad empty interactionId');
    }

    let searchParams: any = {
      types: ['contact_entry'],
      sort: [{ entryDate: { order: 'desc' } }],
      from: 0,
      query: {
        bool: {
          must: [{ term: { uuid: interactionId } }, { term: { status: InteractionStatus[status] } }],
        },
      },
    };

    RxUtil.toPromise(this.elasticSearchService.searchOnSeveralTypesDeprecated(searchParams), this.$q).then(
      response => {
        this.$log.debug('[agent-history] searchInteractionByIdAndStatus got [' + response.hits.total.value + ']interaction in response');
        deferred.resolve({
          contactEntries: ContactHistoryService.buildEntries(response),
          total: response.hits.total.value,
        });
      },
      () => deferred.reject('[agent-history] Error on searchOnSeveralTypes in searchInteractionByIdAndStatus')
    );

    return deferred.promise;
  }

  private appointmentInProcess$(input: SearchParams): Observable<ITimelineEntryResult> {
    // retrieve adresses
    // customer load guard
    const addresses$ = new Observable<string[]>(subscriber => {
      const customerId = 'contact' in input ? input.contact.customerId : '';
      const uri = 'contact' in input ? input.contact.phoneNumber : '';
      if (!customerId && !('companyName' in input)) {
        // strange condition ?!
        if (!uri || uri.indexOf('SOCIAL_NETWORK_BASKET') === -1) {
          subscriber.next([]);
        } else {
          subscriber.next([uri]);
        }
        subscriber.complete();
        return Subscription.EMPTY;
      }

      const customer$ = this.customerFacade.getById(customerId || '');
      const addresses$ = this.customerFacade.getAddressesById(customerId || '');

      // load if needed
      customer$
        .pipe(
          take(1),
          filter(customer => !customer)
        )
        .subscribe(() => this.customerFacade.load(customerId || ''));

      // retrieve the customer's addresses
      return customer$
        .pipe(
          timeout(3000),
          first(customer => !!customer),
          switchMap(() => addresses$.pipe(first()))
        )
        .subscribe({
          next: addresses => {
            subscriber.next(addresses.filter(({ channel }) => channel.type === ChannelType.PHONE).map(({ uri }) => uri));
            subscriber.complete();
          },
          error: () => {
            this.$log.warn(`can't find customer for id [${customerId}]`);
            subscriber.next([]);
            subscriber.complete();
          },
        });
    });
    return addresses$.pipe(
      // get all appointments based on all of the phone numbers
      mergeMap(addresses =>
        combineLatest(
          addresses.map(uri => this.appointmentDeprecatedService.getAppointments(uri, 'companyName' in input ? input.companyName : ''))
        ).pipe(
          map(iappointmentsArray => (<IAppointment[]>[]).concat(...iappointmentsArray)),
          mergeMap((iappointments: IAppointment[]) => iappointments),
          // get the campaigns
          mergeMap((iappointment: IAppointment) =>
            this.appointmentDeprecatedService.getCampaign(iappointment.campaign.id).pipe(
              // if campaign contain slot
              filter((campaign: Campaign) => isDefined(campaign.timeSlots)),
              mergeMap(
                // selector => slot whose match iappointment
                (campaign: Campaign) => observableFrom(campaign.timeSlots.filter(slot => slot.id === iappointment.timeSlot.id)),
                // result selector => map slot in timeline
                (campaign: Campaign, slot: TimeSlot) => {
                  iappointment.campaign = {
                    ...iappointment.campaign,
                    stream: campaign.stream,
                  };
                  iappointment.timeSlot = slot;
                  iappointment.customerCardUuid = 'contact' in input ? Option.of(input.contact.customerId).getOrElse('') : '';
                  let timeline: ITimelineEntry = {
                    id: iappointment.timeSlot.id,
                    canalName: iappointment.campaignType === CampaignType.VISIO_SCHEDULED_CALL_BACK ? 'VISIO' : 'PHONE',
                    canalDirection: 'OUTGOING',
                    entryDate: new Date(slot.beginDate),
                    beginDate: new Date(slot.beginDate),
                    endDate: new Date(slot.endDate),
                    description: iappointment.campaign.name,
                    hasVisibility: true,
                    selected: false,
                    visibilityType: iappointment.visibilityType,
                    appointment: iappointment,
                    chatConversationUuid: '',
                    isNotHandledPhone: false,
                    extraData: {},
                  };
                  return timeline;
                }
              ),
              toArray(),
              // build result
              map((timeline: ITimelineEntry[]) => this.buildTimelineResult(timeline, 'IN_PROCESS'))
            )
          )
        )
      )
    );
  }

  private socialNetworkInProcess$(input: SearchParams): Observable<ITimelineEntryResult> {
    let param;
    param = {
      interactionTypeList: ['PRIMARY', 'LINKED', 'REOPEN'],
      interactionStatusList: [InteractionStatus.OPENED],
      canalName: ChannelType.SOCIAL_NETWORK,
      from: 0,
      size: 50,
    };
    if ('companyName' in input) {
      param.companyName = input.companyName;
    } else {
      param.contact = input.contact;
    }
    return this.searchPreviousContactExchangeByChannel(param).pipe(
      mergeMap((result: IContactEntryResult) => result.contactEntries.filter((entry: IContactEntry) => !!entry.dimeloRequestId)),
      map((entry: IContactEntry) => TimelineEntry.fromContactEntry(entry)),
      toArray(),
      map((timeline: ITimelineEntry[]) => this.buildTimelineResult(timeline, 'IN_PROCESS'))
    );
  }

  private mailInProcess$(input: SearchParams): Observable<ITimelineEntryResult> {
    let entries: { [id: string]: IContactEntry } = {};
    let privateEntries: IContactEntry[] = [];
    let requestVisibility: { [id: string]: boolean } = {};
    let param;
    param = {
      iinteractionTypeList: ['PRIMARY', 'LINKED'],
      interactionStatusList: [InteractionStatus.OPENED, InteractionStatus.IN_APPROVAL],
      canalName: ChannelType.MAIL,
      from: input.from,
      size: input.size,
    };
    if ('companyName' in input) {
      param.companyName = input.companyName;
    } else {
      param.contact = input.contact;
    }

    // first query to retrieve open mail insteraction

    return this.searchPreviousContactExchangeByChannel(param).pipe(
      // second query to match ids found with eptica els
      mergeMap((result: IContactEntryResult) => {
        privateEntries = result.contactEntries.filter(
          (entry: IContactEntry) => entry.visibilityType === ContactEntryVisibilityType.PRIVATE || entry.visibilityType === 'PRIVATE'
        );
        result.contactEntries
          .filter((entry: IContactEntry) => !!entry.epticaRequestId)
          .forEach((entry: IContactEntry) => {
            entry.isEpticaSynchronized = false;
            entries[`${entry.epticaRequestId}`] = entry;
          });
        // get detail with els query for ids given
        return this.epticaDeprecatedService.getMatchingRequestsByIds(Object.keys(entries), 'id', 'asc', 0, input.size).pipe(
          map(requests =>
            requests.map(
              req =>
                ({
                  id: +req.id,
                  responsibleId: req.accountResponsible && +req.accountResponsible.id,
                  subject: req.subject,
                  memo: MemoDtoUtil.buildMemo(extractEpticaField(req.fields, 'awlDifferMemo')),
                } as IAssociatedRequest)
            )
          ),
          // Retrieve visibility
          mergeMap(requests =>
            RxUtil.fromPromise(() =>
              this.getRequestVisibility(requests.map(req => req.id))
                .then(req => (requestVisibility = req))
                .then(() => requests)
            )
          ),
          mergeMap(requests => requests),
          // remove duplicate id
          distinct(request => request.id),
          // transform to history object
          map(request => {
            let myEntry: IContactEntry = entries[request.id];
            let isMail = myEntry.canalName === 'MAIL';
            // here , we know that the entry is ok for Eptica
            myEntry.isEpticaSynchronized = true;
            return TimelineEntry.fromContactEntry(
              myEntry,
              request.subject,
              this.hasVisibility(requestVisibility, `${myEntry.epticaRequestId}`, isMail),
              request.responsibleId,
              request.memo
            );
          }),
          toArray(),
          // build result
          map((timeline: ITimelineEntry[]) => {
            // add "wrong" keys with isEpticaSynchronised to false
            Object.keys(entries).forEach(key => {
              let myEntry = entries[key];
              if (!myEntry.isEpticaSynchronized) {
                timeline.push(TimelineEntry.fromContactEntry(myEntry, 'not synchronized:' + this.getSubject(myEntry).getOrElse(''), false));
              }
            });
            privateEntries.forEach(entry => {
              entry.isEpticaSynchronized = false;
              timeline.push(TimelineEntry.fromContactEntry(entry, '', false));
            });
            return this.buildTimelineResult(timeline, 'IN_PROCESS', result.total);
          })
        );
      })
    );
  }

  private retry(obs$: Observable<ITimelineEntryResult>, entriesToMatch: ITimelineEntry[] = []): Observable<ITimelineEntryResult> {
    return obs$.pipe(
      tap((result: ITimelineEntryResult) => this.$log.debug(`[AgentHistory] find : Try to valid result [${JSON.stringify(result)}]`)),
      // try to valid result, otherwise throw exception
      map((result: ITimelineEntryResult) => {
        if (!result || result.total === 0 || !entriesToMatch || entriesToMatch.length === 0) {
          return result;
        }
        if (entriesToMatch.every(entryToMatch => this.entryPredicate(entryToMatch, result.timeline))) {
          return result;
        }
        throw 'No match';
      }),
      tap(null, err => this.$log.debug(`[Agent History] find : Failed to valid result due to [${err}]`)),
      // if throw no match error well we wait delay and relaunch observable source
      // we try two time, otherwise we throw new exception to continue process
      retryWhen(attempts =>
        attempts.pipe(
          delay(500),
          take(2),
          concatMap(() => throwError(() => new Error()))
        )
      ),
      // if we catch error here, well it's mean retring has failed
      // we return by default the result from observable source (= final try)
      catchError(() => obs$),
      tap((result: ITimelineEntryResult) => this.$log.debug(`[AgentHistory] find : Result found [${JSON.stringify(result)}]`))
    );
  }

  private entryPredicate(entry: ITimelineEntry, timeline: ITimelineEntry[]) {
    return entry.entryDate < timeline[timeline.length - 1].entryDate || timeline.some(e => e.id === entry.id);
  }

  private accumulate(): OperatorFunction<ITimelineEntryResult, ITimelineEntryResult> {
    return pipe(
      // accumulate all event in stream observable and return one occurance
      scan((acc: ITimelineEntryResult, current: ITimelineEntryResult) => ({
        ...current,
        timeline: current.timeline.concat(acc.timeline),
        total: current.total + acc.total,
        type: current.type,
      }))
    );
  }

  private getSubject(entry: IContactEntry): Option<string> {
    return this.getContentValue(entry, 'subject');
  }

  private getContentValue(entry: IContactEntry, myKey: string): Option<string> {
    let found: string[] =
      !!entry.contents && isArray(entry.contents)
        ? entry.contents
            .map((content: IContactEntryContent) =>
              Option.of(content.content)
                .filter(c => c !== '')
                .map(c =>
                  fromJson(c)
                    .filter(row => row.key === myKey)
                    .map(row => row.value)
                    .shift()
                )
                .getOrElse('')
            )
            .filter(val => val !== '')
        : [];

    return found.length > 0 ? Option.of(found[0]) : None;
  }

  private validateMailEvent(event: IEpticaExctractEvent): boolean {
    return Option.of(event)
      .filter(evt => {
        let comment = Option.of(evt.comment).filter(s => s !== '');
        let content = Option.of(evt.content).filter(s => s !== '');
        let skill: Option<IEpticaExtractHistoryData> = Option.of(evt.skill);
        let skillSource = skill
          .flatMap(s => Option.of(s.source))
          .map(skills => skills.map(s => s.name).toString())
          .filter(s => s !== '');
        let skillTarget = skill
          .flatMap(s => Option.of(s.target))
          .map(skills => skills.map(s => s.name).toString())
          .filter(s => s !== '');
        return [comment, content, skillSource, skillTarget].some(op => op.isDefined());
      })
      .isDefined();
  }

  private hasVisibility(visibilityMap: { [id: string]: boolean }, esId: string, isMailEntry: boolean): boolean {
    return esId ? visibilityMap[esId] : !isMailEntry;
  }

  private getRequestVisibility(esRequestids: number[]): IPromise<{ [id: number]: boolean }> {
    if (esRequestids && esRequestids.filter(e => !isNaN(e)).length > 0) {
      return this.epticaDeprecatedService.getRequestVisibility(esRequestids);
    } else {
      return this.$q.resolve({});
    }
  }

  static buildFakeContactEntry(): IContactEntry {
    return {
      id: randomUUID(),
      entryDate: new Date(),
      beginDate: new Date(),
      endDate: new Date(),
      canalName: '',
      canalDirection: '',
      campaignType: '',
      contactUri: '',
      contactFirstName: '',
      contactLastName: '',
      contactCompanyName: '',
      contactUuid: '',
      csrUuid: '',
      csrLogin: '',
      csrFirstname: '',
      csrLastname: '',
      description: '',
      technicalId: '',
      connectionId: '',
      epticaRequestId: '',
      dimeloRequestId: '',
      interactionType: '',
      interactionStatus: '',
      parentInteractionUuid: '',
      extraData: '',
      skills: [],
      contents: [],
      redirected: false,
      visibilityType: ContactEntryVisibilityType.AUTHORIZED,
      isEpticaSynchronized: true,
      chatConversationUuid: '',
      notHandledReason: ContactEntryNotHandledReason.NONE,
      delayBeforeNotHandledReason: 0,
    };
  }
}

export default 'agentHistory';
